import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { fetchGroupsByRestaurant, fetchSessions, InventoryGroup, InventorySessionSummary } from '../../../../services/inventory/inventoryService';
import { borderPrimary, canvasBg, cardBg, greenTextColor, lightGreenBG, mutedText, primaryText, secondaryText, redTextColor } from '../../../../styling/stylingHelper';

const PAGE_SIZE = 50;

export interface InventoryCountSelection {
  groupId: string | null;
  /** The chosen count (closing count for leakage). */
  session: InventorySessionSummary | null;
  /** Leakage only: the opening count, always older than `session`. */
  baseline: InventorySessionSummary | null;
}

interface Props {
  restaurantId: string;
  /** `pair` = leakage: pick the end count, then an older start count. */
  mode: 'single' | 'pair';
  value: InventoryCountSelection;
  onChange: (value: InventoryCountSelection) => void;
}

const sameSession = (a: InventorySessionSummary | null, b: InventorySessionSummary) => !!a && a.sessionId === b.sessionId;

export default function InventoryCountPicker({ restaurantId, mode, value, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const [groups, setGroups] = useState<InventoryGroup[] | null>(null);
  const [sessions, setSessions] = useState<InventorySessionSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    setGroups(null);
    fetchGroupsByRestaurant(restaurantId)
      .then((list) => {
        if (cancelled) return;
        setGroups(list);
        if (list.length > 0) onChangeRef.current({ groupId: list[0].id, session: null, baseline: null });
      })
      .catch((err) => {
        console.error('Failed to load inventory groups:', err);
        if (!cancelled) setError(t('analysisReports.picker.loadError'));
      });
    return () => { cancelled = true; };
  }, [restaurantId, t]);

  const groupId = value.groupId;

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    setSessions([]);
    setLoading(true);
    setError(null);
    fetchSessions(restaurantId, { groupId, limit: PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setSessions(page);
        setHasMore(page.length === PAGE_SIZE);
      })
      .catch((err) => {
        console.error('Failed to load inventory sessions:', err);
        if (!cancelled) setError(t('analysisReports.picker.loadError'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId, groupId, t]);

  const loadMore = async () => {
    if (!groupId || sessions.length === 0) return;
    setLoading(true);
    try {
      const page = await fetchSessions(restaurantId, {
        groupId,
        limit: PAGE_SIZE,
        beforeDate: sessions[sessions.length - 1].completedAt.toISOString(),
      });
      setSessions((prev) => [...prev, ...page]);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more inventory sessions:', err);
      setError(t('analysisReports.picker.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Legacy counts (no session id) can be neither the reported count nor a leakage baseline.
  const selectable = useMemo(() => sessions.filter((s) => s.sessionId), [sessions]);
  const olderThanEnd = useMemo(
    () => (value.session ? selectable.filter((s) => s.completedAt < value.session!.completedAt) : []),
    [selectable, value.session]
  );

  const dateLabel = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }) +
    ` · ${date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}`;

  const sessionList = (list: InventorySessionSummary[], selected: InventorySessionSummary | null, onPick: (s: InventorySessionSummary) => void) => (
    <div className={cn('flex flex-col gap-2 max-h-[320px] overflow-y-auto rounded-xl p-2', canvasBg)}>
      {list.map((s) => {
        const active = sameSession(selected, s);
        return (
          <button
            key={s.sessionId!}
            type="button"
            onClick={() => onPick(s)}
            className={cn(
              'flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
              borderPrimary,
              active ? lightGreenBG : cn(cardBg, 'hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark')
            )}
          >
            <div className="flex flex-col min-w-0">
              <span className={cn('text-sm font-semibold break-words', active ? greenTextColor : primaryText)}>{dateLabel(s.completedAt)}</span>
              <span className={cn('text-xs break-words', mutedText)}>
                {t('analysisReports.picker.sectionCount', { count: s.sectionCount })}
                {s.countedByName ? ` · ${t('analysisReports.picker.countedBy', { name: s.countedByName })}` : ''}
              </span>
            </div>
            {active && <Check className={cn('w-4 h-4 shrink-0', greenTextColor)} />}
          </button>
        );
      })}
      {hasMore && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loading}
          className={cn('rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50', greenTextColor)}
        >
          {t('analysisReports.picker.loadMore')}
        </button>
      )}
    </div>
  );

  const label = (text: string) => <p className={cn('mb-2 text-sm font-semibold', secondaryText)}>{text}</p>;

  if (error) return <p className={cn('text-sm', redTextColor)}>{error}</p>;
  if (groups === null) return <Loader2 className={cn('w-5 h-5 animate-spin', mutedText)} />;
  if (groups.length === 0) return <p className={cn('text-sm', mutedText)}>{t('analysisReports.picker.noGroups')}</p>;

  return (
    <div className="flex flex-col gap-5">
      <div>
        {label(t('analysisReports.picker.group'))}
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => {
            const active = g.id === groupId;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => { if (!active) onChange({ groupId: g.id, session: null, baseline: null }); }}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
                  borderPrimary,
                  active ? cn(lightGreenBG, greenTextColor) : cn(cardBg, secondaryText)
                )}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      {loading && sessions.length === 0 ? (
        <Loader2 className={cn('w-5 h-5 animate-spin', mutedText)} />
      ) : selectable.length === 0 ? (
        <p className={cn('text-sm', mutedText)}>{t('analysisReports.picker.noCounts')}</p>
      ) : (
        <>
          <div>
            {label(t(mode === 'pair' ? 'analysisReports.picker.endCount' : 'analysisReports.picker.count'))}
            {sessionList(selectable, value.session, (s) => onChange({ groupId, session: s, baseline: null }))}
          </div>
          {mode === 'pair' && value.session && (
            <div>
              {label(t('analysisReports.picker.startCount'))}
              {olderThanEnd.length === 0 && !hasMore ? (
                <p className={cn('text-sm', mutedText)}>{t('analysisReports.picker.noOlderCounts')}</p>
              ) : (
                sessionList(olderThanEnd, value.baseline, (s) => onChange({ ...value, baseline: s }))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
